<template>
    <div class="tw:flex tw:flex-col tw:gap-4">
        <!-- Breadcrumbs -->
        <nav v-if="breadcrumbs.length > 1" class="tw:flex tw:items-center tw:gap-1 tw:text-sm">
            <span v-for="(item, index) in breadcrumbs" :key="index" class="tw:inline-flex tw:items-center">
                <button
                    v-if="index < breadcrumbs.length - 1"
                    @click="goTo(item, index)"
                    class="tw:text-blue-600 tw:hover:underline"
                >
                    {{ item.name }}
                </button>
                <span v-else class="tw:text-gray-500">{{ item.name }}</span>
                <span v-if="index < breadcrumbs.length - 1" class="tw:text-gray-400 tw:mx-1">/</span>
            </span>
        </nav>

        <!-- Error state -->
        <div v-if="error" class="tw:text-red-600 tw:text-sm">
            {{ error }}
        </div>

        <!-- Initial section list (no section selected yet) -->
        <div v-if="!seccionSeleccionada && !loading">
            <p class="tw:text-sm tw:text-gray-600 tw:mb-3">Selecciona una seccion en el mapa:</p>
            <SvgSectionsComponent
                :secciones="secciones"
                @clickZona="onSeccionClick"
            />
            <div v-if="secciones.length === 0" class="tw:text-gray-500 tw:text-sm tw:mt-4">
                No hay secciones disponibles para este recinto.
            </div>
        </div>

        <!-- Section selected: show zones -->
        <div v-else-if="seccionSeleccionada && !seccionSeleccionada.tiene_secciones">
            <!-- Back button -->
            <div class="tw:flex tw:justify-between tw:items-center tw:mb-2">
                <span class="tw:text-sm tw:text-gray-600">{{ seccionSeleccionada.name }}</span>
                <Button variant="ghost" icon="fa fa-redo" @click="volver">Volver</Button>
            </div>

            <div v-show="!cargandoZonas" class="tw:flex tw:gap-4 tw:w-full">
                <div class="tw:w-2/3">
                    <SvgSectionComponent
                        :zonas="zonasFormated"
                        :seccion="seccionSeleccionada"
                        @clickZona="onSvgZonaClick"
                        @cargado="cargandoZonas = false"
                    />
                </div>
                <div class="tw:w-1/3">
                    <TablaSectoresComponent
                        :zonas="zonasFormated"
                        @goTo="onTablaGoTo"
                    />
                </div>
            </div>
            <div v-if="cargandoZonas" class="tw:flex tw:justify-center tw:py-8">
                <LoaderComponent />
            </div>
        </div>

        <!-- Section selected with sub-sections (recursive navigation) -->
        <div v-else-if="seccionSeleccionada && seccionSeleccionada.tiene_secciones">
            <div class="tw:flex tw:justify-between tw:items-center tw:mb-2">
                <span class="tw:text-sm tw:text-gray-600">{{ seccionSeleccionada.name }}</span>
                <Button variant="ghost" icon="fa fa-redo" @click="volver">Volver</Button>
            </div>

            <div v-show="!cargandoZonas">
                <SvgSectionComponent
                    :zonas="secciones"
                    :seccion="seccionSeleccionada"
                    @clickZona="onSeccionClick"
                    @cargado="cargandoZonas = false"
                />
            </div>
            <div v-if="cargandoZonas" class="tw:flex tw:justify-center tw:py-8">
                <LoaderComponent />
            </div>
        </div>

        <!-- Loading initial sections -->
        <div v-if="loading" class="tw:flex tw:justify-center tw:py-8">
            <LoaderComponent />
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import store from '@/store';
    import SvgSectionsComponent from '@/components/backend/stepper/abonos/components/secciones/components/SvgSectionsComponent.vue';
    import SvgSectionComponent from '@/components/backend/stepper/abonos/components/secciones/components/SvgSectionComponent.vue';
    import TablaSectoresComponent from '@/components/backend/stepper/components/TablaSectoresComponent.vue';
    import { useCambiarAsientoSecciones } from '@/composables/admin/abonados/useCambiarAsientoSecciones';
    import Button from '@/modules/admin/ui/Button.vue';
    import LoaderComponent from '@/components/preloader/LoaderComponent';

    const emit = defineEmits(['zonaSeleccionada']);

    const lista = computed(() => store.getters.getListaAbonados);
    const recinto = computed(() => store.getters.getRecinto);

    const recintoId = computed(() => recinto.value?.id);
    const listaId = computed(() => lista.value?.id);

    const {
        secciones,
        seccionSeleccionada,
        zonas,
        breadcrumbs,
        loading,
        cargandoZonas,
        error,
        selectSeccion,
        goTo,
        volver
    } = useCambiarAsientoSecciones({ recintoId, listaId });

    /**
     * Format zones for display (mirrors zonas_formated from SeleccionarRegionConSecciones)
     */
    const zonasFormated = computed(() => {
        return zonas.value.map(z => {
            const copia = { ...z };
            copia.esta_disponible = z.tipos_abonos?.length > 0 && z.esta_disponible;
            return copia;
        });
    });

    /**
     * Handle section click from SVG map
     */
    function onSeccionClick(seccion) {
        selectSeccion(seccion);
    }

    /**
     * Handle zone click from SVG map (1 arg: zona)
     */
    function onSvgZonaClick(zona) {
        emit('zonaSeleccionada', zona);
    }

    /**
     * Handle zone click from table list (2 args: paso, zona)
     */
    function onTablaGoTo(_paso, zona) {
        emit('zonaSeleccionada', zona);
    }
</script>
